import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/components/ui/toast";
import { CheckCircle2, Loader2 } from "lucide-react";

import type { FormData } from "@/schemas/formSchema";
import { formSchema } from "@/schemas/formSchema";
import { api } from "@/services/api";
import {
  SOURCE_OPTIONS,
  CITY_OPTIONS,
  WILL_ATTEND_OPTIONS,
  ORDER_STATUS_OPTIONS,
} from "@/types/form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const SOURCE_SELECT_ITEMS = SOURCE_OPTIONS.map((value) => ({
  label: value,
  value,
}));
const CITY_SELECT_ITEMS = CITY_OPTIONS.map((value) => ({
  label: value,
  value,
}));
const TIME_SELECT_ITEMS = [
  { label: "08:00 - 09:00", value: "08:00 - 09:00" },
  { label: "11:00 - 12:00", value: "11:00 - 12:00" },
  { label: "14:00 - 15:00", value: "14:00 - 15:00" },
];

const logoBackgroundStyle: CSSProperties = {
  backgroundImage: "url(/palapa/logo-palapa-transparent.png)",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center right",
};

export default function FormPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
  });

  const onSubmit = async (data: FormData) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setIsSubmitting(true);
      await api.submitForm(data, controller.signal);
      reset();
      setIsSuccess(true);
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error instanceof Error) {
        if (
          error.message.includes("Failed to fetch") ||
          error.message.includes("NetworkError")
        ) {
          toast.add({
            title:
              "Gagal terhubung ke server. Pastikan server backend sedang berjalan.",
            type: "error",
          });
        } else {
          toast.add({ title: error.message, type: "error" });
        }
      } else {
        toast.add({ title: "Terjadi kesalahan", type: "error" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const handleNewSubmission = () => {
    setIsSuccess(false);
    reset();
  };

  return (
    <div className="min-h-screen bg-muted/40 px-4 py-8">
      <main className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-2xl items-center justify-center">
        {isSuccess ? (
          <Card className="w-full max-w-md pt-5 pb-0">
            <CardHeader className="items-center text-center">
              <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 aria-hidden="true" className="size-9" />
              </div>
              <CardTitle className="text-xl">
                Formulir Berhasil Dikirim!
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <Button onClick={handleNewSubmission} className="w-full">
                Kirim Formulir Baru
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="w-full overflow-hidden gap-0 py-0">
            <CardHeader className="relative overflow-hidden rounded-t-2xl bg-primary p-6 text-primary-foreground">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute right-0 top-1/2 size-48 -translate-y-1/2 opacity-10"
                style={logoBackgroundStyle}
              />
              <div className="relative z-10 flex flex-col gap-2">
                <h1 className="text-3xl font-bold tracking-tight">
                  Formulir Inspeksi Kendaraan
                </h1>
                <CardDescription className="text-primary-foreground/80">
                  Silakan lengkapi formulir berikut untuk pemesanan inspeksi.
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="px-6 pt-4 pb-6">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6"
              >
                <FieldSet className="gap-0">
                  <FieldLegend className="mb-0">Informasi Pemesan</FieldLegend>
                  <Separator className="mt-2 mb-4" />
                  <FieldGroup className="grid gap-4 md:grid-cols-2">
                    <Field data-invalid={!!errors.firstName}>
                      <FieldLabel htmlFor="firstName">
                        Nama Depan{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Input
                        id="firstName"
                        aria-invalid={!!errors.firstName}
                        aria-describedby={
                          errors.firstName ? "firstName-error" : undefined
                        }
                        {...register("firstName")}
                      />
                      <FieldError id="firstName-error">
                        {errors.firstName?.message}
                      </FieldError>
                    </Field>

                    <Field data-invalid={!!errors.lastName}>
                      <FieldLabel htmlFor="lastName">
                        Nama Belakang{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Input
                        id="lastName"
                        aria-invalid={!!errors.lastName}
                        aria-describedby={
                          errors.lastName ? "lastName-error" : undefined
                        }
                        {...register("lastName")}
                      />
                      <FieldError id="lastName-error">
                        {errors.lastName?.message}
                      </FieldError>
                    </Field>

                    <Field
                      className="md:col-span-2"
                      data-invalid={!!errors.phone}
                    >
                      <FieldLabel htmlFor="phone">
                        No HP Pemesan{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="08123456789"
                        aria-invalid={!!errors.phone}
                        aria-describedby={
                          errors.phone ? "phone-error" : undefined
                        }
                        {...register("phone")}
                      />
                      <FieldError id="phone-error">
                        {errors.phone?.message}
                      </FieldError>
                    </Field>

                    <Field
                      className="md:col-span-2"
                      data-invalid={!!errors.email}
                    >
                      <FieldLabel htmlFor="email">
                        E-mail{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Input
                        id="email"
                        type="email"
                        placeholder="email@example.com"
                        aria-invalid={!!errors.email}
                        aria-describedby={
                          errors.email ? "email-error" : undefined
                        }
                        {...register("email")}
                      />
                      <FieldError id="email-error">
                        {errors.email?.message}
                      </FieldError>
                    </Field>

                    <Field
                      className="md:col-span-2"
                      data-invalid={!!errors.address}
                    >
                      <FieldLabel htmlFor="address">
                        Alamat Domisili/Tinggal Pemesan{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Textarea
                        id="address"
                        rows={3}
                        placeholder="Alamat lengkap Anda"
                        aria-invalid={!!errors.address}
                        aria-describedby={
                          errors.address ? "address-error" : undefined
                        }
                        {...register("address")}
                      />
                      <FieldError id="address-error">
                        {errors.address?.message}
                      </FieldError>
                    </Field>

                    <Field
                      className="md:col-span-2"
                      data-invalid={!!errors.source}
                    >
                      <FieldLabel htmlFor="source">
                        Dari mana Anda mengetahui layanan inspeksi kami?{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Controller
                        name="source"
                        control={control}
                        render={({ field }) => (
                          <Select
                            items={SOURCE_SELECT_ITEMS}
                            value={field.value ?? null}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              id="source"
                              className="w-full"
                              ref={field.ref}
                              name={field.name}
                              onBlur={field.onBlur}
                              aria-invalid={!!errors.source}
                              aria-describedby={
                                errors.source ? "source-error" : undefined
                              }
                            >
                              <SelectValue placeholder="Pilih sumber informasi" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {SOURCE_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError id="source-error">
                        {errors.source?.message}
                      </FieldError>
                    </Field>

                    <FieldSet
                      className="gap-2 md:col-span-2"
                      data-invalid={!!errors.willAttend || undefined}
                    >
                      <FieldLegend
                        id="willAttend-label"
                        className="mb-0 text-sm font-medium"
                      >
                        Apakah Anda bersedia hadir saat inspeksi?{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLegend>
                      <Controller
                        name="willAttend"
                        control={control}
                        render={({ field }) => (
                          <RadioGroup
                            value={field.value ?? ""}
                            onValueChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            aria-labelledby="willAttend-label"
                            aria-describedby={
                              errors.willAttend ? "willAttend-error" : undefined
                            }
                            aria-invalid={!!errors.willAttend}
                            className="mt-3 flex flex-row gap-6"
                          >
                            {WILL_ATTEND_OPTIONS.map((option) => (
                              <Field
                                key={option}
                                orientation="horizontal"
                                className="w-auto gap-2"
                              >
                                <RadioGroupItem
                                  value={option}
                                  id={`attend-${option}`}
                                  aria-invalid={!!errors.willAttend}
                                />
                                <FieldLabel
                                  htmlFor={`attend-${option}`}
                                  className="cursor-pointer font-normal"
                                >
                                  {option}
                                </FieldLabel>
                              </Field>
                            ))}
                          </RadioGroup>
                        )}
                      />
                      <FieldError id="willAttend-error">
                        {errors.willAttend?.message}
                      </FieldError>
                    </FieldSet>
                  </FieldGroup>
                </FieldSet>

                <FieldSet className="gap-0">
                  <FieldLegend className="mb-0">
                    Informasi Kendaraan
                  </FieldLegend>
                  <Separator className="mt-2 mb-4" />
                  <FieldGroup className="gap-4">
                    <FieldGroup className="grid gap-4 md:grid-cols-2">
                      <Field data-invalid={!!errors.vehicleBrand}>
                        <FieldLabel htmlFor="vehicleBrand">
                          Merek Mobil{" "}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FieldLabel>
                        <Input
                          id="vehicleBrand"
                          placeholder="Toyota"
                          aria-invalid={!!errors.vehicleBrand}
                          aria-describedby={
                            errors.vehicleBrand
                              ? "vehicleBrand-error"
                              : undefined
                          }
                          {...register("vehicleBrand")}
                        />
                        <FieldError id="vehicleBrand-error">
                          {errors.vehicleBrand?.message}
                        </FieldError>
                      </Field>

                      <Field data-invalid={!!errors.vehicleModel}>
                        <FieldLabel htmlFor="vehicleModel">
                          Jenis Mobil{" "}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FieldLabel>
                        <Input
                          id="vehicleModel"
                          placeholder="Avanza"
                          aria-invalid={!!errors.vehicleModel}
                          aria-describedby={
                            errors.vehicleModel
                              ? "vehicleModel-error"
                              : undefined
                          }
                          {...register("vehicleModel")}
                        />
                        <FieldError id="vehicleModel-error">
                          {errors.vehicleModel?.message}
                        </FieldError>
                      </Field>
                    </FieldGroup>

                    <FieldGroup className="grid gap-4 md:grid-cols-3">
                      <Field data-invalid={!!errors.vehicleYear}>
                        <FieldLabel htmlFor="vehicleYear">
                          Tahun Mobil{" "}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FieldLabel>
                        <Input
                          id="vehicleYear"
                          type="number"
                          placeholder="2020"
                          min={1980}
                          max={new Date().getFullYear()}
                          aria-invalid={!!errors.vehicleYear}
                          aria-describedby={
                            errors.vehicleYear ? "vehicleYear-error" : undefined
                          }
                          {...register("vehicleYear", { valueAsNumber: true })}
                        />
                        <FieldError id="vehicleYear-error">
                          {errors.vehicleYear?.message}
                        </FieldError>
                      </Field>

                      <Field data-invalid={!!errors.vehiclePlate}>
                        <FieldLabel htmlFor="vehiclePlate">
                          Plat Mobil{" "}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FieldLabel>
                        <Input
                          id="vehiclePlate"
                          placeholder="AB 1234 CD"
                          aria-invalid={!!errors.vehiclePlate}
                          aria-describedby={
                            errors.vehiclePlate
                              ? "vehiclePlate-error"
                              : undefined
                          }
                          {...register("vehiclePlate")}
                        />
                        <FieldError id="vehiclePlate-error">
                          {errors.vehiclePlate?.message}
                        </FieldError>
                      </Field>

                      <Field data-invalid={!!errors.vehicleColor}>
                        <FieldLabel htmlFor="vehicleColor">
                          Warna Mobil{" "}
                          <span aria-hidden="true" className="text-destructive">
                            *
                          </span>
                        </FieldLabel>
                        <Input
                          id="vehicleColor"
                          placeholder="Putih"
                          aria-invalid={!!errors.vehicleColor}
                          aria-describedby={
                            errors.vehicleColor
                              ? "vehicleColor-error"
                              : undefined
                          }
                          {...register("vehicleColor")}
                        />
                        <FieldError id="vehicleColor-error">
                          {errors.vehicleColor?.message}
                        </FieldError>
                      </Field>
                    </FieldGroup>
                  </FieldGroup>
                </FieldSet>

                <FieldSet className="gap-0">
                  <FieldLegend className="mb-0">
                    Lokasi & Jadwal Inspeksi
                  </FieldLegend>
                  <Separator className="mt-2 mb-4" />
                  <FieldGroup className="grid gap-4 md:grid-cols-2">
                    <Field
                      className="md:col-span-2"
                      data-invalid={!!errors.serviceCity}
                    >
                      <FieldLabel htmlFor="serviceCity">
                        Kota Layanan{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Controller
                        name="serviceCity"
                        control={control}
                        render={({ field }) => (
                          <Select
                            items={CITY_SELECT_ITEMS}
                            value={field.value ?? null}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              id="serviceCity"
                              className="w-full"
                              ref={field.ref}
                              name={field.name}
                              onBlur={field.onBlur}
                              aria-invalid={!!errors.serviceCity}
                              aria-describedby={
                                errors.serviceCity
                                  ? "serviceCity-error"
                                  : undefined
                              }
                            >
                              <SelectValue placeholder="Pilih kota layanan" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {CITY_OPTIONS.map((city) => (
                                  <SelectItem key={city} value={city}>
                                    {city}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError id="serviceCity-error">
                        {errors.serviceCity?.message}
                      </FieldError>
                    </Field>

                    <Field
                      className="md:col-span-2"
                      data-invalid={!!errors.vehicleLocation}
                    >
                      <FieldLabel htmlFor="vehicleLocation">
                        Lokasi Mobil Saat Ini{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Textarea
                        id="vehicleLocation"
                        rows={3}
                        placeholder="Alamat lengkap lokasi mobil"
                        aria-invalid={!!errors.vehicleLocation}
                        aria-describedby={
                          errors.vehicleLocation
                            ? "vehicleLocation-error"
                            : undefined
                        }
                        {...register("vehicleLocation")}
                      />
                      <FieldError id="vehicleLocation-error">
                        {errors.vehicleLocation?.message}
                      </FieldError>
                    </Field>

                    <Field data-invalid={!!errors.inspectionDate}>
                      <FieldLabel htmlFor="inspectionDate">
                        Tanggal Inspeksi{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Input
                        id="inspectionDate"
                        type="date"
                        aria-invalid={!!errors.inspectionDate}
                        aria-describedby={
                          errors.inspectionDate
                            ? "inspectionDate-error"
                            : undefined
                        }
                        {...register("inspectionDate")}
                      />
                      <FieldError id="inspectionDate-error">
                        {errors.inspectionDate?.message}
                      </FieldError>
                    </Field>

                    <Field data-invalid={!!errors.inspectionTime}>
                      <FieldLabel htmlFor="inspectionTime">
                        Jam Inspeksi{" "}
                        <span aria-hidden="true" className="text-destructive">
                          *
                        </span>
                      </FieldLabel>
                      <Controller
                        name="inspectionTime"
                        control={control}
                        render={({ field }) => (
                          <Select
                            items={TIME_SELECT_ITEMS}
                            value={field.value ?? null}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger
                              id="inspectionTime"
                              className="w-full"
                              ref={field.ref}
                              name={field.name}
                              onBlur={field.onBlur}
                              aria-invalid={!!errors.inspectionTime}
                              aria-describedby={
                                errors.inspectionTime
                                  ? "inspectionTime-error"
                                  : undefined
                              }
                            >
                              <SelectValue placeholder="Pilih jam inspeksi" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                {TIME_SELECT_ITEMS.map((option) => (
                                  <SelectItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError id="inspectionTime-error">
                        {errors.inspectionTime?.message}
                      </FieldError>
                    </Field>
                  </FieldGroup>
                </FieldSet>

                <FieldSet
                  className="gap-2"
                  data-invalid={!!errors.orderStatus || undefined}
                >
                  <FieldLegend
                    id="orderStatus-label"
                    className="mb-0 text-sm font-medium"
                  >
                    Status Pemesanan{" "}
                    <span aria-hidden="true" className="text-destructive">
                      *
                    </span>
                  </FieldLegend>
                  <Controller
                    name="orderStatus"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        value={field.value ?? ""}
                        onValueChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        aria-labelledby="orderStatus-label"
                        aria-describedby={
                          errors.orderStatus ? "orderStatus-error" : undefined
                        }
                        aria-invalid={!!errors.orderStatus}
                        className="mt-3 flex flex-col gap-3"
                      >
                        {ORDER_STATUS_OPTIONS.map((option) => (
                          <Field
                            key={option}
                            orientation="horizontal"
                            className="w-auto gap-2"
                          >
                            <RadioGroupItem
                              value={option}
                              id={`order-${option}`}
                              aria-invalid={!!errors.orderStatus}
                            />
                            <FieldLabel
                              htmlFor={`order-${option}`}
                              className="cursor-pointer font-normal"
                            >
                              {option}
                            </FieldLabel>
                          </Field>
                        ))}
                      </RadioGroup>
                    )}
                  />
                  <FieldError id="orderStatus-error">
                    {errors.orderStatus?.message}
                  </FieldError>
                </FieldSet>

                <FieldSet className="gap-0">
                  <FieldLegend className="mb-0">
                    Informasi Tambahan (Opsional)
                  </FieldLegend>
                  <Separator className="mt-2 mb-4" />
                  <FieldGroup className="grid gap-4 md:grid-cols-2">
                    <Field className="md:col-span-2">
                      <FieldLabel htmlFor="ownerContact">
                        Nama Pemilik Mobil dan Nomor HP
                      </FieldLabel>
                      <Input
                        id="ownerContact"
                        placeholder="Nama pemilik - 08123456789"
                        {...register("ownerContact")}
                      />
                      <FieldDescription>
                        Hanya digunakan apabila customer susah dihubungi saat
                        jadwal pengecekan
                      </FieldDescription>
                    </Field>

                    <Field className="md:col-span-2">
                      <FieldLabel htmlFor="notes">
                        Catatan/Keterangan untuk Inspektor
                      </FieldLabel>
                      <Textarea
                        id="notes"
                        rows={3}
                        placeholder="Catatan tambahan (opsional)"
                        {...register("notes")}
                      />
                    </Field>
                  </FieldGroup>
                </FieldSet>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                  className="h-12 w-full text-base"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2
                        aria-hidden="true"
                        data-icon="inline-start"
                        className="size-4 animate-spin"
                      />
                      Mengirim...
                    </>
                  ) : (
                    "Kirim Data"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
